import { useChat } from '@livekit/components-react'
import { useEffect, useRef, useMemo } from 'react'
import { ChatMessageInput } from './ChatMessageInput'
import { useAllTranscriptions } from './useAllTranscriptions'

interface ConversationMessage {
  type: 'chat' | 'transcription'
  timestamp: number
  from: string
  text: string
  isLocal: boolean
}

export function ConversationTile() {
  const { chatMessages, send } = useChat()
  const messagesEndRef = useRef<HTMLDivElement>(null)

  // Get all transcriptions from all audio tracks
  const transcriptionMessages = useAllTranscriptions()

  // Combine chat messages and transcriptions into a single chronological list
  const conversationMessages = useMemo(() => {
    const messages: ConversationMessage[] = []

    // Add chat messages
    chatMessages.forEach(msg => {
      messages.push({
        type: 'chat',
        timestamp: msg.timestamp,
        from: msg.from?.name || msg.from?.identity || 'Unknown',
        text: msg.message,
        isLocal: msg.from?.isLocal || false,
      })
    })

    // Add transcription messages
    transcriptionMessages.forEach(msg => {
      messages.push({
        type: 'transcription',
        ...msg,
      })
    })

    // Sort by timestamp
    return messages.sort((a, b) => a.timestamp - b.timestamp)
  }, [chatMessages, transcriptionMessages])

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [conversationMessages])

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto px-3 py-4 space-y-3">
        {conversationMessages.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Conversation will appear here...
          </p>
        ) : (
          conversationMessages.map((message, index) => (
            <div
              key={`${message.type}-${message.timestamp}-${index}`}
              className={`flex flex-col gap-1 ${
                message.isLocal ? 'items-end' : 'items-start'
              }`}
            >
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-muted-foreground">
                  {message.from}
                </span>
                <span className="text-xs text-muted-foreground">
                  {new Date(message.timestamp).toLocaleTimeString()}
                </span>
                {message.type === 'transcription' && (
                  <span className="text-xs text-muted-foreground italic">
                    (spoken)
                  </span>
                )}
              </div>
              <div
                className={`rounded-lg px-3 py-2 max-w-[80%] ${
                  message.isLocal
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-foreground'
                }`}
              >
                <p className="text-sm break-words">{message.text}</p>
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>
      <div className="border-t border-border bg-background px-3 py-3">
        <ChatMessageInput
          onSend={message => {
            send(message)
          }}
        />
      </div>
    </div>
  )
}
